import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import MonogramLogo from "../assets/images/SDMono.svg";
import TopFrame from "../assets/images/TopFrame.svg";
import "../assets/Cover.css";
import GoldenFlower from "../assets/images/Golden-Flower.svg";
import MixedFlower from "../assets/images/Flower-Mix.svg";
import Midflower from "../assets/images/mid.svg";
import Butterfly from "./Butterfly";
import LeafFall from "./Leaf";
import Name from "./Name";

const Cover = () => {
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  const handleEnter = () => {
    setLeaving(true);
  };

  // 🎞 Background entrance and exit animation
  const pageVariants = {
    initial: { opacity: 0, scale: 1.05 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.2, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 1.2,
      filter: "blur(10px)",
      transition: { duration: 1, ease: "easeInOut" },
    },
  };

  // ✨ Element fade-in staggered
  const containerVariants = {
    animate: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: "easeOut" },
    },
    exit: { opacity: 0, y: -40, transition: { duration: 0.8, ease: "easeIn" } },
  };

  // 🦋 Helper for random range
  const rand = (min, max) => Math.random() * (max - min) + min;

  // 🦋 Create butterflies with soft motion
  const butterflies = [];
  for (let i = 0; i < 18; i++) {
    const direction = Math.random() > 0.5 ? "left" : "right";
    const size = rand(0.5, 1.3);
    const duration = rand(5, 8);
    const delay = Math.random() * 2;
    const colorType = Math.random() > 0.5 ? "pink" : "white";

    butterflies.push(
      <motion.div
        key={i}
        style={{
          position: "absolute",
          top: `${Math.random() * 80}%`,
          left: `${Math.random() * 100}%`,
          zIndex: 2,
        }}
        animate={{
          y: [0, rand(-30, 30), 0],
          x: direction === "left" ? [0, rand(-40, -10), 0] : [0, rand(10, 40), 0],
          rotate: [0, rand(10, 20), rand(-10, -20), 0],
        }}
        transition={{
          repeat: Infinity,
          duration,
          ease: "easeInOut",
          delay,
        }}
      >
        <Butterfly direction={direction} size={size} colorType={colorType} />
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      {!leaving && (
        <motion.div
          key="cover"
          className="bg-Cover"
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          <LeafFall />

          {/* 🌸 Top Frame */}
          <motion.img
            src={TopFrame}
            alt="Top Frame"
            className="top-frame"
            variants={fadeUp}
            initial="initial"
            animate="animate"
          />

          {/* 🦋 Butterflies */}
          {butterflies}

          {/* 🌼 Monogram Section */}
          <motion.div
            className="Monogram"
            variants={containerVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
            }}
          >
            <motion.h2 className="monogram1-text" variants={fadeUp}>
              សិរីមង្គលអាពាហ៍ពិពាហ៍
            </motion.h2>
            <motion.h2 className="monogram1-text-english" variants={fadeUp}>
              The Wedding Day
            </motion.h2>
            <motion.img
              src={MonogramLogo}
              alt="Monogram Logo"
              className="monogram-logo"
              variants={fadeUp}
            />
          </motion.div>

          {/* 💌 Invitation Text */}
          <motion.div
            className="invitation-text"
            variants={containerVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              width: "100%",
            }}
          >
            <motion.h4 variants={fadeUp}>សូមគោរពអញ្ជើញ</motion.h4>
            <motion.h2 variants={fadeUp}>
              <Name />
            </motion.h2>
          </motion.div>

          {/* 🌷 Enter Button */}
          <motion.div
            className="cover-button"
            variants={fadeUp}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <motion.button
              onClick={handleEnter}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 200, damping: 12 }}
            >
              បើកធៀប
            </motion.button>
          </motion.div>

          {/* 🌺 Golden Flowers */}
          <div className="golden-flower-container">
            <img src={GoldenFlower} alt="Golden Flower" className="golden-flower left" />
            <img src={GoldenFlower} alt="Golden Flower" className="golden-flower right" />
          </div>

          {/* 🌸 Bottom flowers */}
          <div className="mix-flower-container1 bottom">
            <div className="ellipse-behind"></div>
            <img src={MixedFlower} alt="Flower Left" className="mix-flower left" />
            <img src={Midflower} alt="Flower Center" className="mix-flower center" />
            <img src={MixedFlower} alt="Flower Right" className="mix-flower right" />
          </div>
        </motion.div>
      )}

      {/* 💫 Exit Overlay (smooth fade to pink) */}
      {leaving && (
        <motion.div
          className="exit-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          onAnimationComplete={() => navigate("/main")}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          
            zIndex: 9999,
          }}
        />
      )}
    </AnimatePresence>
  );
};

export default Cover;
